import { useDispatch } from 'react-redux';
import { useAppDispatch } from '../../../../../store';
import { 
  updateScheduleSettingsForecastDaily, 
  updateScheduleSettingsActualDaily, 
  updateScheduleSettingsForecastZoneDistribution, 
  updateScheduleSettingsActualZoneDistribution, 
  updateScheduleSettingsHistoricalHours, 
  fetchShiftTimes, 
  fetchScheduleForecast 
} from '../../../../../store/thunks/dloThunks';
import { 
  setForecastDSDTableData, 
  // setForecastNONDSDTableData, // Commented out - UI table is disabled
  setActualDSDTableData, 
  // setActualNONDSDTableData, // Commented out - UI table is disabled
  setForecastDSDZoneTableData, 
  setForecastNONDSDZoneTableData, 
  setActualDSDZoneTableData, 
  setActualNONDSDZoneTableData, 
  setHistoricalHoursTableData, 
  setShiftTimesTableData, 
  setShiftTimesData, 
  setScheduleForecastTableData 
} from '../../../../../store/slices/dloSlice';
import { extractDailyPercentagesFromTableData, extractDailyActualDataFromTableData, extractZoneDistributionUpdates, extractHistoricalHoursUpdates } from '../utils/dataExtractors';
import { validatePercentages, validateActualDataUpdates } from '../utils/validators';
import { ModalState } from '../types';

interface UseDLOScheduleHandlersProps {
  forecastDSDTableData: any[];
  forecastNONDSDTableData: any[];
  actualDSDTableData: any[];
  actualNONDSDTableData: any[];
  forecastDSDZoneTableData: any[];
  forecastNONDSDZoneTableData: any[];
  actualDSDZoneTableData: any[];
  actualNONDSDZoneTableData: any[];
  historicalHoursTableData: any[];
  selectedLocation: any;
  setModalState: (modal: ModalState) => void;
}

export const useDLOScheduleHandlers = ({
  forecastDSDTableData,
  forecastNONDSDTableData,
  actualDSDTableData,
  actualNONDSDTableData,
  forecastDSDZoneTableData,
  forecastNONDSDZoneTableData,
  actualDSDZoneTableData,
  actualNONDSDZoneTableData,
  historicalHoursTableData,
  selectedLocation,
  setModalState,
}: UseDLOScheduleHandlersProps) => {
  const dispatch = useAppDispatch();

  // Handle save for DSD Percentage Table
  const handleSaveDSDPercentage = (updates?: any[]) => {
    // If updates are provided by the component (already validated and prepared with day-level tracking),
    // use them directly. Otherwise, fall back to extracting from Redux state.
    const finalUpdates = updates || extractDailyPercentagesFromTableData(forecastDSDTableData);
    
    console.log('handleSaveDSDPercentage: Received updates from component:', updates ? 'YES' : 'NO');
    console.log('handleSaveDSDPercentage: Final updates to dispatch:', finalUpdates);
    
    if (!validatePercentages(finalUpdates)) {
      setModalState({
        visible: true,
        type: 'error',
        title: 'Validation Error',
        message: 'The sum of all daily percentages must not exceed 100%.'
      });
      return;
    }

    dispatch(updateScheduleSettingsForecastDaily({ type: 'DSD', updates: finalUpdates }));
  };

  // Handle save for NON-DSD Percentage Table - Commented out since UI table is disabled
  // const handleSaveNONDSDPercentage = (currentTableData: any[]) => {
  //   const updates = extractDailyPercentagesFromTableData(forecastNONDSDTableData);
  //   
  //   if (!validatePercentages(updates)) {
  //     setModalState({
  //       visible: true,
  //       type: 'error',
  //       title: 'Validation Error',
  //       message: 'The sum of all daily percentages must not exceed 100%.'
  //     });
  //     return;
  //   }
  //
  //   dispatch(updateScheduleSettingsForecastDaily({ type: 'NON-DSD', updates }));
  // };

  // Handle save for Actual DSD Table
  const handleSaveActualDSDData = (updates?: any[]) => {
    // If updates are provided by the component (already validated and prepared with day-level tracking),
    // use them directly. Otherwise, fall back to extracting from Redux state.
    const finalUpdates = updates || extractDailyActualDataFromTableData(actualDSDTableData);
    
    console.log('handleSaveActualDSDData: Received updates from component:', updates ? 'YES' : 'NO');
    console.log('handleSaveActualDSDData: Final updates to dispatch:', finalUpdates);
    
    if (!validateActualDataUpdates(finalUpdates)) {
      setModalState({
        visible: true,
        type: 'error',
        title: 'Validation Error',
        message: 'All 7 days are required for updates.'
      });
      return;
    }

    dispatch(updateScheduleSettingsActualDaily({ type: 'DSD', updates: finalUpdates }));
  };

  // Handle save for Actual NON-DSD Table - Commented out since UI table is disabled
  // const handleSaveActualNONDSDData = (currentTableData: any[]) => {
  //   const updates = extractDailyActualDataFromTableData(actualNONDSDTableData);
  //   
  //   if (!validateActualDataUpdates(updates)) {
  //     setModalState({
  //       visible: true,
  //       type: 'error',
  //       title: 'Validation Error',
  //       message: 'All 7 days are required for updates.'
  //     });
  //     return;
  //   }
  //
  //   dispatch(updateScheduleSettingsActualDaily({ type: 'NON-DSD', updates }));
  // };

  // Zone distribution handlers
  const handleSaveDSDZoneDistribution = async (updates?: any[]) => {
    try {
      // If updates are provided by the component (already validated and prepared),
      // use them directly. Otherwise, fall back to extracting from Redux state.
      const finalUpdates = updates || extractZoneDistributionUpdates(forecastDSDZoneTableData, false);
      
      console.log('handleSaveDSDZoneDistribution: Received updates from component:', updates ? 'YES' : 'NO');
      console.log('handleSaveDSDZoneDistribution: Final updates to dispatch:', finalUpdates);
      
      await dispatch(updateScheduleSettingsForecastZoneDistribution({
        type: 'DSD',
        updates: finalUpdates
      })).unwrap();
      
      // After successful save, update Redux table data to mark saved days as having backend overrides
      // This ensures subsequent saves will correctly identify days with backend data
      const updatedTableData = forecastDSDZoneTableData.map(zone => {
        // Create a map of which days were saved with non-null overridePct
        const savedDaysOverrides: Record<string, boolean> = {};
        finalUpdates.forEach((update: any) => {
          if (update.vpZone === zone.zoneName.toUpperCase() && update.overridePct !== null) {
            // Convert dayNum back to day key (1 -> sun, 2 -> mon, etc.)
            const dayKey = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][update.dayNum - 1];
            if (dayKey) {
              savedDaysOverrides[dayKey] = true;
            }
          }
        });
        
        // Merge with existing backend overrides
        return {
          ...zone,
          __actualOverrides: {
            ...zone.__actualOverrides,
            ...savedDaysOverrides
          }
        };
      });
      
      dispatch(setForecastDSDZoneTableData(updatedTableData));
      console.log('✅ Updated Redux state with new backend overrides for DSD zone distribution');
    } catch (error) {
      console.error('Error saving DSD Zone Volume Distribution:', error);
    }
  };

  const handleSaveNONDSDZoneDistribution = async (updates?: any[]) => {
    try {
      // If updates are provided by the component (already validated and prepared),
      // use them directly. Otherwise, fall back to extracting from Redux state.
      const finalUpdates = updates || extractZoneDistributionUpdates(forecastNONDSDZoneTableData, false);
      
      console.log('handleSaveNONDSDZoneDistribution: Received updates from component:', updates ? 'YES' : 'NO');
      console.log('handleSaveNONDSDZoneDistribution: Final updates to dispatch:', finalUpdates);
      
      await dispatch(updateScheduleSettingsForecastZoneDistribution({
        type: 'NON-DSD',
        updates: finalUpdates
      })).unwrap();
      
      // After successful save, update Redux table data to mark saved days as having backend overrides
      const updatedTableData = forecastNONDSDZoneTableData.map(zone => {
        // Create a map of which days were saved with non-null overridePct
        const savedDaysOverrides: Record<string, boolean> = {};
        finalUpdates.forEach((update: any) => {
          if (update.vpZone === zone.zoneName.toUpperCase() && update.overridePct !== null) {
            // Convert dayNum back to day key (1 -> sun, 2 -> mon, etc.)
            const dayKey = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][update.dayNum - 1];
            if (dayKey) {
              savedDaysOverrides[dayKey] = true;
            }
          }
        });
        
        // Merge with existing backend overrides
        return {
          ...zone,
          __actualOverrides: {
            ...zone.__actualOverrides,
            ...savedDaysOverrides
          }
        };
      });
      
      dispatch(setForecastNONDSDZoneTableData(updatedTableData));
      console.log('✅ Updated Redux state with new backend overrides for NON-DSD zone distribution');
    } catch (error) {
      console.error('Error saving NON-DSD Zone Volume Distribution:', error);
    }
  };

  const handleSaveActualDSDZoneDistribution = async () => {
    try {
      const updates = extractZoneDistributionUpdates(actualDSDZoneTableData, true);
      await dispatch(updateScheduleSettingsActualZoneDistribution({
        type: 'DSD',
        updates
      })).unwrap();
      
      // After successful save, update Redux table data to mark saved days as having backend overrides
      const updatedTableData = actualDSDZoneTableData.map(zone => {
        // Create a map of which days were saved with non-null overridePct
        const savedDaysOverrides: Record<string, boolean> = {};
        updates.forEach((update: any) => {
          if (update.vpZone === zone.zoneName.toUpperCase() && update.overridePct !== null) {
            // Convert dayNum back to day key (1 -> sun, 2 -> mon, etc.)
            const dayKey = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][update.dayNum - 1];
            if (dayKey) {
              savedDaysOverrides[dayKey] = true;
            }
          }
        });
        
        // Merge with existing backend overrides
        return {
          ...zone,
          __actualOverrides: {
            ...zone.__actualOverrides,
            ...savedDaysOverrides
          }
        };
      });
      
      dispatch(setActualDSDZoneTableData(updatedTableData));
      console.log('✅ Updated Redux state with new backend overrides for Actual DSD zone distribution');
    } catch (error) {
      console.error('Error saving Actual DSD Zone Volume Distribution:', error);
    }
  };

  const handleSaveActualNONDSDZoneDistribution = async () => {
    try {
      const updates = extractZoneDistributionUpdates(actualNONDSDZoneTableData, true);
      await dispatch(updateScheduleSettingsActualZoneDistribution({
        type: 'NON-DSD',
        updates
      })).unwrap();
      
      // After successful save, update Redux table data to mark saved days as having backend overrides
      const updatedTableData = actualNONDSDZoneTableData.map(zone => {
        // Create a map of which days were saved with non-null overridePct
        const savedDaysOverrides: Record<string, boolean> = {};
        updates.forEach((update: any) => {
          if (update.vpZone === zone.zoneName.toUpperCase() && update.overridePct !== null) {
            // Convert dayNum back to day key (1 -> sun, 2 -> mon, etc.)
            const dayKey = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][update.dayNum - 1];
            if (dayKey) {
              savedDaysOverrides[dayKey] = true;
            }
          }
        });
        
        // Merge with existing backend overrides
        return {
          ...zone,
          __actualOverrides: {
            ...zone.__actualOverrides,
            ...savedDaysOverrides
          }
        };
      });
      
      dispatch(setActualNONDSDZoneTableData(updatedTableData));
      console.log('✅ Updated Redux state with new backend overrides for Actual NON-DSD zone distribution');
    } catch (error) {
      console.error('Error saving Actual NON-DSD Zone Volume Distribution:', error);
    }
  };

  // Historical Hours handlers
  const handleSaveHistoricalHours = async () => {
    try {
      console.log('Saving Historical Hours data...');
      
      if (!selectedLocation?.id) {
        setModalState({
          visible: true,
          type: 'error',
          title: 'Error',
          message: 'No location selected'
        });
        return;
      }

      const dataToSave = extractHistoricalHoursUpdates(historicalHoursTableData);
      console.log('Historical Hours updates to save:', dataToSave);

      const result = await dispatch(updateScheduleSettingsHistoricalHours({
        updates: dataToSave
      }));

      if (updateScheduleSettingsHistoricalHours.fulfilled.match(result)) {
        setModalState({
          visible: true,
          type: 'success',
          title: 'Success',
          message: 'Fixed Hours data saved successfully'
        });
      } else {
        setModalState({
          visible: true,
          type: 'error',
          title: 'Error',
          message: 'Failed to save Historical Hours data'
        });
      }
    } catch (error) {
      console.error('Error saving Historical Hours:', error);
      setModalState({
        visible: true,
        type: 'error',
        title: 'Error',
        message: 'Failed to save Historical Hours data'
      });
    }
  };

  const handleSaveHistoricalHoursRow = async (rowId: string) => {
    try {
      console.log('Saving Historical Hours row:', rowId);
      
      if (!selectedLocation?.id) {
        setModalState({
          visible: true,
          type: 'error',
          title: 'Error',
          message: 'No location selected'
        });
        return;
      }

      const rowToSave = historicalHoursTableData.find((row: any) => row.id === rowId);
      
      if (!rowToSave) {
        setModalState({
          visible: true,
          type: 'error',
          title: 'Error',
          message: 'Row data not found'
        });
        return;
      }

      const dataToSave = extractHistoricalHoursUpdates([rowToSave]);
      console.log('Historical Hours row updates to save:', dataToSave);

      const result = await dispatch(updateScheduleSettingsHistoricalHours({
        updates: dataToSave
      }));

      if (updateScheduleSettingsHistoricalHours.fulfilled.match(result)) {
        setModalState({
          visible: true,
          type: 'success',
          title: 'Success',
          message: 'Fixed Hours row saved successfully'
        });
      } else {
        setModalState({
          visible: true,
          type: 'error',
          title: 'Error',
          message: 'Failed to save Historical Hours row'
        });
      }
    } catch (error) {
      console.error('Error saving Historical Hours row:', error);
      setModalState({
        visible: true,
        type: 'error',
        title: 'Error',
        message: 'Failed to save Historical Hours row'
      });
    }
  };

  // Shift times handlers
  const handleSaveShiftTimes = async () => {
    console.log('🔄 Shift times save callback triggered - DLOTable has already saved to API');
    
    try {
      const result = await dispatch(fetchShiftTimes());
      if (fetchShiftTimes.fulfilled.match(result)) {
        dispatch(setShiftTimesData(result.payload));
        console.log('✅ Refreshed shift times data after save');
      }
    } catch (error) {
      console.error('❌ Error refreshing shift times after save:', error);
    }
  };

  const handleClearShiftTimes = () => {
    console.log('Clearing shift times...');
    dispatch(setShiftTimesTableData([]));
    setModalState({
      visible: true,
      type: 'success',
      title: 'Success',
      message: 'Shift times cleared successfully'
    });
  };

  const retryShiftTimes = () => {
    dispatch(fetchShiftTimes());
  };

  // Volume Forecast handlers
  const handleSaveVolumeForecast = (tableData: any[]) => {
    console.log('Volume Forecast table saved:', tableData);
    dispatch(setScheduleForecastTableData(tableData));
    // Note: Do not show modal here as DLOVolumeForecast component already shows its own success modal
  };

  const retryScheduleForecast = () => {
    dispatch(fetchScheduleForecast());
  };

  // Generic handlers
  const handleClearHistoricalHoursRow = (rowId: string) => {
    console.log('Clearing Historical Hours row:', rowId);
    // TODO: Implement row-level clear for Historical Hours
  };

  const handleRestoreHistoricalHoursRow = (rowId: string) => {
    console.log('Restoring Historical Hours row:', rowId);
    // TODO: Implement row-level restore for Historical Hours
  };

  const handleAddZoneHistoricalHours = () => {
    console.log('Adding new zone to Historical Hours');
    // TODO: Implement add zone functionality for Historical Hours
  };

  const handleZoneAddedHistoricalHours = (zone: string) => {
    console.log('Zone added to Historical Hours:', zone);
    // TODO: Implement zone added callback for Historical Hours
  };

  const handleRowSaveVolumeForecast = (rowId: string) => {
    console.log('Volume Forecast row saved:', rowId);
    // TODO: Implement row-level save for Volume Forecast
  };

  const handleRowClearVolumeForecast = (rowId: string) => {
    console.log('Volume Forecast row cleared:', rowId);
    // TODO: Implement row-level clear for Volume Forecast
  };

  const handleRowDeleteVolumeForecast = (rowId: string) => {
    console.log('Volume Forecast row deleted:', rowId);
    // TODO: Implement row-level delete for Volume Forecast
  };

  return {
    handleSaveDSDPercentage,
    // handleSaveNONDSDPercentage, // Commented out - UI table is disabled
    handleSaveActualDSDData,
    // handleSaveActualNONDSDData, // Commented out - UI table is disabled
    handleSaveDSDZoneDistribution,
    handleSaveNONDSDZoneDistribution,
    handleSaveActualDSDZoneDistribution,
    handleSaveActualNONDSDZoneDistribution,
    handleSaveHistoricalHours,
    handleSaveHistoricalHoursRow,
    handleClearHistoricalHoursRow,
    handleRestoreHistoricalHoursRow,
    handleAddZoneHistoricalHours,
    handleZoneAddedHistoricalHours,
    handleSaveShiftTimes,
    handleClearShiftTimes,
    retryShiftTimes,
    handleSaveVolumeForecast,
    handleRowSaveVolumeForecast,
    handleRowClearVolumeForecast,
    handleRowDeleteVolumeForecast,
    retryScheduleForecast,
  };
};
